import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getChatApiClient } from "@shared/apis/sidekick/api-factories";
import {
  ActiveWidgetContext,
  SendPromptResponse,
} from "@shared/apis/sidekick/generated";
import { appEventEmitter } from "@shared/events/app-event-emitter";
import { buildGetMessagesKey } from "./useGetMessages";
import { toast } from "@shared/services/toast";

type Props = {
  onSuccess(data: SendPromptResponse, variables: { threadId: string }): void;
  onError?(error: unknown, variables: { threadId: string }): void;
};

type Params = {
  threadId: string;
  message: string;
  hidden?: boolean;
  activeWidgetContext?: ActiveWidgetContext;
};

export const useSendStreamMessage = (props: Props) => {
  const { onSuccess, onError } = props;
  const chatApi = getChatApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({
      threadId,
      message,
      hidden,
      activeWidgetContext,
    }: Params) => {
      return chatApi.chatControllerSendPrompt({
        thread: threadId,
        sendMessageRequest: {
          message,
          hidden,
          activeWidgetContext,
        },
      });
    },
    onSuccess: async (data, { threadId }) => {
      onSuccess(data, { threadId });
      appEventEmitter.emit({
        eventType: "sidekick-prompt-request-success",
      });
    },
    onError: (error, { threadId }) => {
      console.error("Failed to send a new message", error);
      appEventEmitter.emit({ eventType: "sidekick-prompt-request-failure" });
      toast.error(
        "Oops! Something went wrong while sending your message. Please try again or contact support if the issue persists.",
        {
          toastId: "send-message-error",
          autoClose: 8000,
        },
      );
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: buildGetMessagesKey({ threadId }),
        });
      }, 1000);
      onError?.(error, { threadId });
    },
    mutationKey: ["send-message"],
  });

  return mutation;
};
